import { asyncLoadImage, getUrlDataForFrame, makePromiseCancelable, videoFrameExtractor } from "components/util/PlaylistUtils";
import ZoomableImage from "components/ZoomableImage";
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { Icon, Tooltip } from "antd";
import OBCSpinner from "components/util/OBC";
import OneFrameIcon from "../../../icons/one-frame.svg";

const csrfTokenSelector = (state) => state.csrfToken;

const getUrlDataForImage = (index, subIndex, small, videoKey = null, imageKeys = [], isStills, isEnhanced, baseURL, csrfToken) => {
    if (index !== -1) {
        let selectedKey = imageKeys[index];
        if (selectedKey) {
            let imageKey = selectedKey[0];

            let imageFile = imageKey;
            let range = null;

            if (isStills) {
                const urlFrameData = getUrlDataForFrame(imageKeys, index, subIndex, videoKey);
                imageFile = urlFrameData.imageFile;
                range = urlFrameData.range;
            } else {
                if (isEnhanced === "enhanced") {
                    imageFile += ".enh.jpg";
                }

                if (small) {
                    imageFile += ".sml.jpg";
                }
            }

            let url = baseURL + imageFile;
            if (csrfToken) {
                url += `?csrf=${csrfToken}`;
            }

            return {
                url,
                range,
                cacheKey: `${url}.${subIndex}`,
            };
        }
    }
    return null;
};

export const getIndexByDirection = (direction, imageKeys, index, subIndex) => {
    let nextIndex;
    let nextSubIndex;

    nextIndex = index;
    nextSubIndex = subIndex + direction;

    const playlistItem = imageKeys[nextIndex];
    if (!videoFrameExtractor(playlistItem) || nextSubIndex > videoFrameExtractor(playlistItem)) {
        nextIndex += direction;
        nextSubIndex = 1;
    } else if (nextSubIndex <= 0) {
        if (nextIndex > 0) {
            nextIndex -= 1;
            const playlistItem = imageKeys[nextIndex];
            if (videoFrameExtractor(playlistItem)) {
                nextSubIndex = videoFrameExtractor(playlistItem);
            } else {
                nextSubIndex = 1;
            }
        } else {
            nextIndex = 0;
            nextSubIndex = 1;
        }
    }
    return { newIndex: nextIndex, newSubIndex: nextSubIndex };
};

const StandAloneImagePlayer = ({ playlist, playlistUrls, index = 0, setIndex = (e) => {}, subIndex = 1, setSubIndex = (e) => {} }) => {
    const [rawImage, setRawImage] = useState(null);
    const [loading, setLoading] = useState(false);
    // const [sourceIndex, setSourceIndex] = useState(0);
    const sourceIndex = 0;

    const [playerHovered, setPlayerHovered] = useState(false);
    const overlayHoverTimeout = useRef(null);

    const imageCache = useRef({});
    const imageLoadPromisesRef = useRef([]);

    const csrfTokenRef = useRef(null);
    const csrfToken = useSelector(csrfTokenSelector);

    const video = useMemo(() => {
        if (playlist && playlist.video_playlists) {
            return playlist.video_playlists[sourceIndex];
        }
        return [];
    }, [playlist, sourceIndex]);

    // update csrf token ref
    useEffect(() => {
        csrfTokenRef.current = csrfToken;

        return () => {
            csrfTokenRef.current = null;
        };
    }, [csrfToken]);

    const baseURL = useMemo(() => {
        if (playlistUrls && playlistUrls.base) {
            return playlistUrls.base;
        }
        return null;
    }, [playlistUrls]);

    const getFromCache = useCallback((imageURL) => {
        return imageCache.current[imageURL];
    }, []);

    const isInCache = useCallback((imageURL) => {
        return imageCache.current.hasOwnProperty(imageURL);
    }, []);

    const addToCache = useCallback(
        (imageURL, imageData) => {
            if (!isInCache(imageURL)) {
                imageCache.current[imageURL] = imageData;
                imageLoadPromisesRef.current.push(imageURL);

                if (imageLoadPromisesRef.current.length > 15) {
                    const urlToRemove = imageLoadPromisesRef.current.shift();
                    delete imageCache.current[urlToRemove];
                }
            }
        },
        [isInCache],
    );

    const precacheImage = useCallback(
        (imageUrlData) => {
            const imageCacheKey = imageUrlData.cacheKey;
            const imageURL = imageUrlData.url;
            const imageRange = imageUrlData.range;

            const imageLoadCallback = (imageData) => {
                addToCache(imageCacheKey, imageData);
            };
            return asyncLoadImage(imageURL, imageRange, imageLoadCallback);
        },
        [addToCache],
    );

    const loadImage = useCallback(
        (imageUrlData) => {
            const imageCacheKey = imageUrlData.cacheKey;

            if (isInCache(imageCacheKey)) {
                return new Promise((resolve) => {
                    resolve(getFromCache(imageCacheKey));
                });
            } else {
                return precacheImage(imageUrlData);
            }
        },
        [isInCache, getFromCache, precacheImage],
    );

    const adjust = (index, subIndex, time, imageKeys) => {
        let playlistItem = imageKeys[index];

        let frameCount = videoFrameExtractor(playlistItem);
        if (!frameCount) {
            frameCount = 1;
        }
        let frameDuration = playlistItem[2];

        let newIndex = index;

        const subIndexTimeOffset = ((subIndex - 1) / frameCount) * frameDuration;
        let targetTimeOffset = subIndexTimeOffset + time;
        while (targetTimeOffset < 0) {
            newIndex -= 1;
            if (newIndex < 0) {
                newIndex = 0;
                targetTimeOffset = 0;
                break;
            }
            playlistItem = imageKeys[newIndex];
            frameDuration = playlistItem[2];
            targetTimeOffset += frameDuration;
        }
        while (targetTimeOffset >= frameDuration) {
            targetTimeOffset -= frameDuration;
            newIndex += 1;
            if (newIndex >= imageKeys.length) {
                newIndex -= 1;
                targetTimeOffset = imageKeys[newIndex][2];
                break;
            }
            playlistItem = imageKeys[newIndex];
            frameDuration = playlistItem[2];
        }

        targetTimeOffset += 0.001;

        frameCount = videoFrameExtractor(playlistItem);

        const frameRatio = targetTimeOffset / frameDuration;
        let newSubIndex;

        if (!frameCount) {
            if (frameRatio >= 0.5 && newIndex < imageKeys.length - 1) {
                newIndex += 1;
            }
            newSubIndex = 1;
        } else if (frameRatio < 0) {
            newSubIndex = 1;
        } else if (frameRatio >= 1) {
            newSubIndex = frameCount;
        } else {
            newSubIndex = Math.floor(frameRatio * frameCount) + 1;
        }
        return { newIndex, newSubIndex };
    };

    const imageLoadComplete = useCallback(
        (index, subIndex, imageData, imageKeys) => {
            setLoading(false);
            setRawImage(imageData);

            let precache;
            precache = ({ newIndex, newSubIndex }) => {
                const frameData = getUrlDataForImage(newIndex, newSubIndex, false, null, imageKeys, true, false, baseURL, csrfTokenRef.current);
                if (frameData && !isInCache(frameData.cacheKey)) {
                    precacheImage(frameData).catch(() => {});
                }
            };

            precache(adjust(index, subIndex, 1, imageKeys));
            precache(adjust(index, subIndex, -1, imageKeys));
            precache(adjust(index, subIndex, 10, imageKeys));
            precache(adjust(index, subIndex, -10, imageKeys));
            precache(adjust(index, subIndex, 60, imageKeys));
            precache(adjust(index, subIndex, -60, imageKeys));

            precache(getIndexByDirection(1, imageKeys, index, subIndex));
            precache(getIndexByDirection(-1, imageKeys, index, subIndex));
        },
        [baseURL, precacheImage],
    );

    const preload = useCallback(
        async (index, subIndex, small, videoKey, imageKeys, baseURL) => {
            if (index === -1) return;

            const selectedKey = imageKeys[index];
            if (!selectedKey) return;

            const imageUrlData = getUrlDataForImage(index, subIndex, small, videoKey, imageKeys, true, false, baseURL, csrfTokenRef.current);
            if (!imageUrlData || !imageUrlData.url) {
                return;
            }

            small = false;
            const cancelableLoadImage = makePromiseCancelable(loadImage(imageUrlData));
            const imageData = await cancelableLoadImage.promise;
            imageLoadComplete(index, subIndex, imageData, imageKeys);
        },
        [imageLoadComplete, loadImage],
    );

    const selectIndex = useCallback(
        (newIndex, newSubIndex, force = false, imageKeys) => {
            if (newIndex >= 0 && newIndex < imageKeys.length && (newIndex !== index || newSubIndex !== subIndex || force)) {
                setIndex(newIndex);
                setSubIndex(newSubIndex);
                setLoading(true);
                preload(newIndex, newSubIndex, false, null, imageKeys, baseURL);
            }
        },
        [index, subIndex, setIndex, setSubIndex, preload, baseURL],
    );

    const forwardByTime = (time) => {
        let { newIndex, newSubIndex } = adjust(index, subIndex, time, video);

        selectIndex(newIndex, newSubIndex, true, video);
    };

    const backwardByTime = (time) => {
        let { newIndex, newSubIndex } = adjust(index, subIndex, -time, video);
        selectIndex(newIndex, newSubIndex, true, video);
    };

    const nextFrame = () => {
        let { newIndex, newSubIndex } = getIndexByDirection(1, video, index, subIndex);
        selectIndex(newIndex, newSubIndex, false, video);
    };

    const prevFrame = () => {
        let { newIndex, newSubIndex } = getIndexByDirection(-1, video, index, subIndex);
        selectIndex(newIndex, newSubIndex, false, video);
    };

    // initial load
    useEffect(() => {
        if (video && video.length > 0) {
            selectIndex(index, subIndex, true, video);
        }
    }, [index, selectIndex, subIndex, video]);

    const nextDisabled = useMemo(() => {
        if (index === video.length - 1) {
            let targetKey = video[index];
            let frameCount = 0;
            if (targetKey) {
                frameCount = videoFrameExtractor(targetKey);
            }
            if (frameCount) {
                if (subIndex >= frameCount) {
                    return true;
                }
            } else {
                return true;
            }
        }
        return false;
    }, [index, subIndex, video]);

    const prevDisabled = useMemo(() => {
        if (index === 0 && subIndex === 1) {
            return true;
        }
        return false;
    }, [index, subIndex]);

    return (
        <div
            className="StandAloneImageComponent"
            onMouseEnter={() => {
                setPlayerHovered(true);
                if (overlayHoverTimeout.current) {
                    clearTimeout(overlayHoverTimeout.current);
                    overlayHoverTimeout.current = null;
                }
            }}
            onMouseLeave={() => {
                overlayHoverTimeout.current = setTimeout(() => {
                    setPlayerHovered(false);
                }, 3000);
            }}>
            {/* LEFT NAVIGATION */}
            <div className={`StandAloneImageComponentNavigationLeft ${playerHovered ? "hovered" : ""}`}>
                {/* Previous frame */}
                <Tooltip
                    title={prevDisabled ? "No More Frames" : "Previous Frame"}
                    placement="right">
                    <div
                        className={`NavigationButton ${prevDisabled ? "disabled" : ""}`}
                        onClick={() => prevFrame()}>
                        <img
                            className="NavigationButtonCustom Left"
                            alt="previous"
                            src={OneFrameIcon}
                            onClick={() => prevFrame()}
                            crossOrigin={"anonymous"}
                        />
                    </div>
                </Tooltip>

                {/* 1 second */}
                <Tooltip
                    title={prevDisabled ? "No More Frames" : "Backward 1 Second"}
                    placement="right">
                    <div
                        className={`NavigationButton ${prevDisabled ? "disabled" : ""}`}
                        onClick={() => backwardByTime(1)}>
                        <span>1s</span>
                        <Icon type="backward" />
                    </div>
                </Tooltip>

                {/* 10 seconds */}
                <Tooltip
                    title={prevDisabled ? "No More Frames" : "Backward 10 Seconds"}
                    placement="right">
                    <div
                        className={`NavigationButton ${prevDisabled ? "disabled" : ""}`}
                        onClick={() => backwardByTime(10)}>
                        <span>10s</span>
                        <Icon type="backward" />
                    </div>
                </Tooltip>
            </div>

            {/* LOADING OVERLAY */}
            {loading && (
                <div className="StandAloneImageComponentLoadingOverlay">
                    <OBCSpinner
                        size={100}
                        speed={3}
                        colorScheme="mono"
                    />
                </div>
            )}

            {/* ZOOMABLE IMAGE */}
            <ZoomableImage
                zoom={1}
                imgSrc={rawImage}
            />

            {/* RIGHT NAVIGATION */}
            <div className={`StandAloneImageComponentNavigationRight ${playerHovered ? "hovered" : ""}`}>
                {/* next frame */}
                <Tooltip
                    title={nextDisabled ? "No More Frames" : "Next Frame"}
                    placement="left">
                    <div
                        className={`NavigationButton ${nextDisabled ? "disabled" : ""}`}
                        onClick={() => nextFrame()}>
                        <img
                            className="NavigationButtonCustom"
                            alt="next"
                            src={OneFrameIcon}
                            onClick={() => nextFrame()}
                            crossOrigin={"anonymous"}
                        />
                    </div>
                </Tooltip>

                {/* 1 second */}
                <Tooltip
                    title={nextDisabled ? "No More Frames" : "Forward 1 Second"}
                    placement="left">
                    <div
                        className={`NavigationButton ${nextDisabled ? "disabled" : ""}`}
                        onClick={() => forwardByTime(1)}>
                        <span>1s</span>
                        <Icon type="forward" />
                    </div>
                </Tooltip>

                {/* 10 seconds */}
                <Tooltip
                    title={nextDisabled ? "No More Frames" : "Forward 10 Seconds"}
                    placement="left">
                    <div
                        className={`NavigationButton ${nextDisabled ? "disabled" : ""}`}
                        onClick={() => forwardByTime(10)}>
                        <span>10s</span>
                        <Icon type="forward" />
                    </div>
                </Tooltip>
            </div>
        </div>
    );
};

export default StandAloneImagePlayer;
